import { createListenerMiddleware } from "@reduxjs/toolkit";
import { notifyNotification } from "../../../effects-bridge";
import {
  getAllNewOnlineOrders,
  getOtherNotifications,
  getAllNewOnlinePollingOrdersSuccess,
} from "../../slices/NotificationsSlice";
import { getLocalStorage, setLocalStorage } from "../../../utils/localStorage";
import { LOCALSTORAGE_KEYS, MILI_SECS_IN_ONE_SEC } from "../../../utils/constants";
import store from "../..";
import { isOrderingLicense } from "../../../helpers/saleHelpers";

/**
 * WEB-ONLY module — `redux-store/middleware/notificationsListener`. One variant: the other
 * platform has no notifications middleware at all (verified with `ls`, per rule 4), which is
 * also why the toast this file raises is wired on one side only.
 *
 * ⚠️ Three substitutions against the app original, all standing on this branch:
 *   - the toast goes through the effects bridge's `notifyNotification`. That entry was added for
 *     THIS module: it is not a styling variant of `notifySuccess` — it dismisses the previous
 *     notification toast and starts a looping audio clip. ⚠️ Only the TOAST half is still beyond
 *     core: the ringer itself was uplifted (`utils/notificationAudio`, 2026-08-26). Do NOT call
 *     `playSound` beside this — the app's toast plays its own inline `Audio`, so it would ring
 *     twice. See the bridge entry's 2026-09-04 note;
 *   - the polling dispatch goes through core's own `store.dispatch()` where the app file imported
 *     the store singleton with `import store from ".."`. That import was a CYCLE with the store
 *     module the middleware is installed into; the bridge is what breaks it;
 *   - `window` is read DIRECTLY, at module scope. That is hazard 18(b), not a violation of
 *     hazard 18(a): the assignment must happen at module-eval time (the header calls it before
 *     any effect runs) and the bridge is not wired yet at that point — but this file is
 *     `.desktop`, so it only ever evaluates under webpack, in a real browser.
 *
 * ⚠️ **Three DEAD imports were dropped** (rule 2b / hazard 13): `getAllOtherNotifications`,
 * `clearAllNotifications` and `getAllNewOnlineOrdersSuccess`. The first two appear only inside
 * the commented-out lines in `startPolling`, which are kept verbatim below because they document
 * what the poll used to do; the third was never referenced at all. Comments create no bindings,
 * so dropping the imports changes nothing.
 */

const notificationsListener = createListenerMiddleware();

function isWithinLastSeconds(dateString: string, seconds: number) {
  const createdTimestamp = new Date(dateString).getTime();
  const currentTimestamp = Date.now();
  const diffInSeconds = (currentTimestamp - createdTimestamp) / 1000;

  return diffInSeconds >= 0 && diffInSeconds <= seconds;
}

const MAX_NOTIFICATION_AGE_SECONDS = 600; // in seconds

let pollingIntervalId: any = null;

export function startPolling() {
  if (pollingIntervalId) return;
  pollingIntervalId = setInterval(() => {
    // dispatch(clearAllNotifications());
    // dispatch(getAllOtherNotifications({ pageSize: 20 }));
    if (!isOrderingLicense())
      store.dispatch(getAllNewOnlineOrders({ autoRefresh: true, skipGlobalLoader: true }));
  }, 10 * MILI_SECS_IN_ONE_SEC);
}

export function stopPolling() {
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
}
// Expose helpers to toggle polling; Header will call via dispatching a DOM event
(window as any).__toggleNotificationsPolling = (enable: boolean) => {
  setLocalStorage(
    LOCALSTORAGE_KEYS.notificationsPolling,
    enable ? "true" : "false"
  );
  if (enable) startPolling();
  else stopPolling();
};

export const setupNotificationsListener = () => {
  const isPollingOn =
    getLocalStorage(LOCALSTORAGE_KEYS.notificationsPolling) === "true";
  if (isPollingOn) {
    // kick off polling on init
    startPolling();
  }

  // Watch for toggle changes via a lightweight action - piggyback on notifications fetch success
  const stopListener1 = notificationsListener.startListening({
    actionCreator: getOtherNotifications,
    effect: async (_action, listenerApi: any) => {
      const prev = listenerApi.getOriginalState();
      const curr = listenerApi.getState();
      const prevList =
        prev?.notificationsStore?.allOtherNotifications?.results || [];
      const currList =
        curr?.notificationsStore?.allOtherNotifications?.results || [];
      const prevIds = new Set(
        (prevList || []).map((n: any) => n?._id || n?.messageId)
      );
      const hasNew = (currList || []).some((n: any) => {
        const id = n?._id || n?.messageId;
        return (
          !!id &&
          !prevIds.has(id) &&
          (!n?.createdDate ||
            isWithinLastSeconds(n?.createdDate, MAX_NOTIFICATION_AGE_SECONDS))
        );
      });
      if (hasNew) notifyNotification("New notifications available");
    },
  });

  const stopListener2 = notificationsListener.startListening({
    actionCreator: getAllNewOnlinePollingOrdersSuccess,
    effect: async (_action, listenerApi: any) => {
      const prev = listenerApi.getOriginalState();
      const curr = listenerApi.getState();
      const prevList = prev?.notificationsStore?.allNewOnlinePollingOrders || [];
      const currList = curr?.notificationsStore?.allNewOnlinePollingOrders || [];
      const prevIds = new Set((prevList || []).map((n: any) => n?._id));
      const hasNew = (currList || []).some(
        (n: any) =>
          !!n?._id &&
          !prevIds.has(n._id) &&
          (!n?.createdDate ||
            isWithinLastSeconds(n?.createdDate, MAX_NOTIFICATION_AGE_SECONDS))
      );
      if (hasNew) notifyNotification("New notifications available");
    },
  });

  return () => {
    stopListener1();
    stopListener2();
    stopPolling();
  };
};

export default notificationsListener;
